// Origin isolation (security-posture Phase 1 — admin.pexify.net split).
//
// The admin shell is served from its OWN origin (admin.pexify.net) so an XSS on
// any seller/supply surface cannot reach the admin JWT (localStorage is
// per-origin). This module is the FE half: each build is stamped with WHICH
// origin it is (VITE_SHELL_ORIGIN), and the guard bounces a route that belongs
// to the other origin over to it — so the admin token only ever lands in the
// admin origin's storage.
//
// Fully inert unless BOTH origins are configured at build time (dev + maket /
// single-SPA prod keep the existing behavior — no redirects).

const ADMIN_ORIGIN = (import.meta.env.VITE_ADMIN_ORIGIN as string | undefined)?.replace(
  /\/$/,
  '',
)
export const APP_ORIGIN = (import.meta.env.VITE_APP_ORIGIN as string | undefined)?.replace(
  /\/$/,
  '',
)

// Which origin THIS build serves: 'admin' (admin.pexify.net) or 'app'
// (pexify.net/app — seller + supply). Unset → 'app' (back-compat single SPA).
const SHELL_ORIGIN = (import.meta.env.VITE_SHELL_ORIGIN as string | undefined) ?? 'app'

/** Origin isolation is active only when both target origins are wired. */
export const ORIGIN_ISOLATION = Boolean(ADMIN_ORIGIN && APP_ORIGIN)

export const IS_ADMIN_ORIGIN = SHELL_ORIGIN === 'admin'

function shellOfPath(pathname: string): 'admin' | 'app' | null {
  if (pathname.startsWith('/admin')) return 'admin'
  if (pathname.startsWith('/seller') || pathname.startsWith('/supply')) return 'app'
  return null
}

/**
 * If `pathname` belongs to the OTHER origin, return the absolute URL to send the
 * browser to (origin base + the same path). Returns null when no cross-origin
 * redirect is needed (isolation off, neutral route, or already correct origin).
 */
export function crossOriginRedirect(
  pathname: string,
  options?: { viewingAsOrg?: boolean },
): string | null {
  if (!ORIGIN_ISOLATION) return null
  const target = shellOfPath(pathname)
  if (target === null) return null
  if (target === SHELL_ORIGIN) return null
  // A platform admin viewing a customer's organisation stays on the ADMIN
  // origin and renders the seller/supply surface here.
  //
  // Bouncing them to the app origin looked right — same path, same backend —
  // but the view-as context lives in this origin's storage and does not travel
  // with the browser, so the other origin opened with whatever session IT had:
  // for Volkan, his own PEXIFY Internal dashboard, with no banner to say the
  // chosen organisation had been dropped (hit live 2026-09-21, entering Osman's
  // org showed the internal store's 971 orders).
  //
  // Sending the context across instead would mean sending the admin token with
  // it, which is exactly what the origin split exists to prevent. Keeping the
  // surface here keeps the token where it belongs; the org is carried by the
  // X-Act-As-Org header the backend already honours.
  if (options?.viewingAsOrg && SHELL_ORIGIN === 'admin' && target === 'app') return null
  const base = target === 'admin' ? ADMIN_ORIGIN : APP_ORIGIN
  return `${base}${pathname}`
}
